import {
  Button,
  Combobox,
  Divider,
  InlineAlert,
  Label,
  Skeleton,
  TextField,
  UtilityButton,
} from '@lego/connect-components-react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { FormEditorModal } from '@/pages/form/FormEditorModal';
import { useFormEditor } from '@/pages/form/hooks';
import type { FormListItem } from '@/pages/form/types';
import { useUserStore } from '@/stores/user-store';
import { canEditForms } from '@/utils/permissions';
import { useFormPrintData } from './hooks';
import { VariableSetRow } from './VariableSetRow';

// ── Edit action (only mounted for users with edit permission) ────────────

const FormEditAction = ({ formDetails }: { formDetails: FormListItem }) => {
  const { t } = useTranslation();
  const editor = useFormEditor();

  return (
    <>
      <Button
        variant="primary"
        appearance="ghost"
        size="small"
        iconName="pen"
        iconPosition="start"
        className="ml-auto"
        onClick={() => editor.openEdit(formDetails)}
      >
        {t('formPage.action.edit')}
      </Button>
      <FormEditorModal editor={editor} />
    </>
  );
};

// ── Loading state ────────────────────────────────────────────────────────

const FormPrintLoading = () => {
  const { t } = useTranslation();
  return (
    <div
      className="min-h-full bg-core-slate-50"
      role="status"
      aria-label={t('formPrintPage.loading', 'Loading...')}
    >
      <div className="border-b border-core-slate-100 bg-core-white px-400 py-300">
        <Skeleton width="12rem" height="1.5rem" showAnimation />
      </div>
      <div className="mx-auto flex w-full max-w-[1400px] flex-col gap-400 px-400 py-500">
        <Skeleton width="16rem" height="2rem" showAnimation />
        <div className="grid grid-cols-1 items-start gap-400 lg:grid-cols-2">
          <div className="flex flex-col gap-300 rounded-150 border border-core-slate-100 bg-core-white p-400">
            <Skeleton width="10rem" height="1.5rem" showAnimation />
            <Skeleton rows={5} rowSize="large" showAnimation />
          </div>
          <Skeleton height="20rem" showAnimation />
        </div>
      </div>
    </div>
  );
};

// ── Component ────────────────────────────────────────────────────────────

export const FormPrintPage = () => {
  const [searchParams] = useSearchParams();
  const formId = searchParams.get('formId');

  if (!formId) return <Navigate to="/form" replace />;

  return <FormPrintContent formId={formId} />;
};

const FormPrintContent = ({ formId }: { formId: string }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useUserStore((s) => s.user);
  const canEdit = user && canEditForms(user);
  const {
    isLoading,
    isError,
    formDetailsLoading,
    formDetails,
    labelRevisionData,
    labelName,
    selectedPrinter,
    selectedRevision,
    printerOptions,
    revisionOptions,
    variables,
    visibleFields,
    readonlyFields,
    disabledFields,
    variableDisplayNameMap,
    validationErrors,
    ruleEffects,
    isPrinting,
    isQuantityOnly,
    areMandatoryFieldsFilled,
    handleRevisionChange,
    handlePrinterChange,
    handleVariableChange,
    handleRemoveVariableSet,
    handleAddVariableSet,
    handlePrint,
  } = useFormPrintData(formId);

  if (isLoading || formDetailsLoading) return <FormPrintLoading />;

  if (isError || !formDetails) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <InlineAlert variant="negative">
          <InlineAlert.Title>{t('formPrintPage.error.loadFormDetails')}</InlineAlert.Title>
        </InlineAlert>
      </div>
    );
  }

  const showPreview =
    (formDetails.showLabelPreview ?? true) && labelRevisionData?.preview && selectedPrinter;

  return (
    <div className="min-h-full overflow-y-scroll bg-core-slate-50">
      {/* Header */}
      <header className="sticky top-0 z-10 flex items-center gap-300 border-b border-core-slate-100 bg-core-white px-400 py-300">
        <UtilityButton
          iconName="arrow-left"
          aria-label={t('formPage.display.formsTitle')}
          onClick={() => navigate('/form')}
        />
        <span className="text-body-sm font-medium text-core-slate-1300">{formDetails.name}</span>
        {canEdit && <FormEditAction formDetails={formDetails} />}
      </header>

      <div className="mx-auto flex w-full max-w-[1400px] flex-col gap-400 px-400 py-500">
        {/* Form title */}
        <div className="flex flex-col gap-100">
          <h1 className="text-heading-md font-brand font-bold text-core-slate-1300">
            {formDetails.name}
          </h1>
          {formDetails.description && (
            <p className="text-body-sm text-core-slate-1100 max-w-prose">
              {formDetails.description}
            </p>
          )}
        </div>

        {/* Two-column grid: left (config/variables) + right (preview) */}
        <div className="grid grid-cols-1 items-start gap-400 lg:grid-cols-[1fr_1fr]">
          {/* Left column */}
          <div className="flex flex-col gap-300 rounded-150 border border-core-slate-100 bg-core-white p-400">
            {/* Configuration section */}
            <div className="flex flex-col gap-300">
              <Label>{t('formPrintPage.configSection')}</Label>

              <TextField
                size="medium"
                readOnly
                value={labelName || '-'}
                aria-label={t('printJobsPage.display.label')}
              />

              <div className="grid grid-cols-1 gap-200 md:grid-cols-2">
                <div className="flex-1">
                  <Label>{t('newPrintPage.display.selectRevision')}</Label>
                  <Combobox
                    fullWidth
                    placeholder={t('newPrintPage.display.selectRevision')}
                    size="medium"
                    value={selectedRevision ? [selectedRevision] : []}
                    onChange={(_e, items) => handleRevisionChange(items[0]?.value ?? '')}
                  >
                    {revisionOptions.map((o) => (
                      <Combobox.Option key={o.value} value={o.value}>
                        {o.label}
                      </Combobox.Option>
                    ))}
                  </Combobox>
                </div>

                <div className="flex-1">
                  <Label>{t('newPrintPage.display.selectPrinter')}</Label>
                  <Combobox
                    fullWidth
                    placeholder={t('newPrintPage.display.choosePrinter')}
                    size="medium"
                    value={selectedPrinter ? [selectedPrinter.toString()] : []}
                    onChange={(_e, items) => handlePrinterChange(items[0]?.value ?? '')}
                  >
                    {printerOptions.map((o) => (
                      <Combobox.Option key={o.value} value={o.value}>
                        {o.label}
                      </Combobox.Option>
                    ))}
                  </Combobox>
                </div>
              </div>

              {ruleEffects.length > 0 && selectedPrinter && (
                <InlineAlert variant="informative">
                  <InlineAlert.Title>{t('formPrintPage.activeFormRules')}</InlineAlert.Title>
                  <InlineAlert.Content>
                    <div className="flex flex-wrap gap-x-400 gap-y-100">
                      {ruleEffects.map((e) => (
                        <span key={`${e.fieldName}-${e.action}`} className="text-body-xs">
                          <span className="font-medium">{e.fieldName}:</span> {e.action}
                          {e.value !== undefined ? ` → "${e.value}"` : ''}
                        </span>
                      ))}
                    </div>
                  </InlineAlert.Content>
                </InlineAlert>
              )}
            </div>

            {/* Variables + Quantity section */}
            {selectedPrinter && (
              <>
                <Divider direction="horizontal" variant="subtle" />

                <div className="flex flex-col gap-300">
                  {!isQuantityOnly && <Label>{t('newPrintPage.display.fillVariables')}</Label>}

                  <div className="flex flex-col gap-300">
                    {variables.map(({ id, ...variable }, index) => (
                      <VariableSetRow
                        key={id as string}
                        id={id as string}
                        index={index}
                        showIndex={variables.length > 1}
                        variable={variable}
                        visibleFields={visibleFields}
                        readonlyFields={readonlyFields}
                        disabledFields={disabledFields}
                        variableDisplayNameMap={variableDisplayNameMap}
                        validationErrors={validationErrors}
                        quantity={Number(variable.quantity ?? 1)}
                        showRemove={variables.length > 1}
                        onRemove={handleRemoveVariableSet}
                        onChange={handleVariableChange}
                        onQuantityChange={(rowId, value) =>
                          handleVariableChange(rowId, 'quantity', value)
                        }
                      />
                    ))}
                  </div>

                  {!isQuantityOnly && (
                    <Button
                      variant="primary"
                      appearance="ghost"
                      size="small"
                      iconName="control-plus"
                      iconPosition="start"
                      onClick={handleAddVariableSet}
                    >
                      {t('formPrintPage.addVariableSet')}
                    </Button>
                  )}
                </div>
              </>
            )}

            {/* Print action */}
            <div className="pt-200">
              <Button
                variant="primary"
                size="medium"
                fullWidth
                disabled={
                  !selectedPrinter ||
                  !labelRevisionData ||
                  isPrinting ||
                  !areMandatoryFieldsFilled ||
                  variables.some((v) => (Number(v.quantity) || 0) < 1)
                }
                onClick={handlePrint}
                loading={isPrinting}
                loadingText={t('newPrintPage.display.printing')}
              >
                {t('newPrintPage.display.print')}
              </Button>
            </div>
          </div>

          {/* Right column: label preview (sticky on large, stacked below on small) */}
          {showPreview && (
            <div className="lg:sticky lg:top-[65px]">
              <div className="flex flex-col items-center gap-300 rounded-150 border border-core-slate-100 bg-core-white p-400">
                <Label>{t('newPrintPage.display.labelPreview')}</Label>
                <div className="flex w-full items-center justify-center overflow-hidden rounded-150 border border-core-slate-100 bg-core-white p-300">
                  <img
                    src={`data:image/png;base64,${labelRevisionData.preview}`}
                    alt="Label Preview"
                    className="max-w-full object-contain"
                  />
                </div>
                <span className="text-body-xs font-medium text-core-slate-1100">
                  {t('formPrintPage.labelSize', 'Label size:')} {labelRevisionData.width} ×{' '}
                  {labelRevisionData.height} mm
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
